import {
  ShippingLocationsSearchDocument,
  useAllBranchesQuery,
  useLocationFieldQuery,
} from '@/buyers/_gen/gql'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import LocationModal from '@/buyers/components/LocationModal'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import AddressM from '@/buyers/modules/Address'
import Action from '@/gf/components/Action'
import FieldError from '@/gf/components/next/forms/FieldError'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import LocationSelector from './LocationSelector'

const LocationField = ({
  label,
  desc,
  locationId,
  onLocationIdChange,
  errors,
}: {
  label: string
  desc?: string
  locationId: string | null
  onLocationIdChange: (id: string | null) => void
  errors?: { locationId?: string }
}) => {
  const [addingLocation, setAddingLocation] = useState(false)
  const client = useGqlClient()
  const branches = useAllBranchesQuery({ variables: { value: '' }, client }).data?.allBranches

  const {
    organization: { id: orgId, requireBillingCompany },
    features,
  } = useSession()

  const selectedLocation = useLocationFieldQuery({
    variables: { orgId, filter: JSON.stringify(['id_eq', locationId]) },
    client,
    skip: !locationId,
  }).data?.org?.locations.at(0)

  const incompleteLocationAddress =
    !selectedLocation?.address?.lineOne ||
    !selectedLocation?.address?.city ||
    !selectedLocation?.address?.state ||
    !selectedLocation?.address?.postalCode ||
    !selectedLocation?.address?.country

  return (
    <div className="flex items-start space-x-2 max-w-md">
      <LocationModal
        open={addingLocation}
        refetchQueries={[ShippingLocationsSearchDocument]}
        onClose={() => setAddingLocation(false)}
        onComplete={(locId) => onLocationIdChange(locId)}
        branches={(requireBillingCompany ? branches : []) ?? []}
        buyers={[]}
        initialAddress={AddressM.init()}
        showPersist
      />

      <div className="space-y-2 flex-grow">
        <h5 className="leading-6">{label}</h5>
        {desc && <p className="prose text-sm">{desc}</p>}

        {selectedLocation ? (
          incompleteLocationAddress ? (
            <>
              <p className="text-gray-600 text-sm leading-5 pt-2">{selectedLocation?.name}</p>
              <Link
                to={`/locations/${selectedLocation?.id}?edit=true`}
                className="inline-flex items-center px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors"
              >
                <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 text-yellow-600" />
                Incomplete address - click to update
              </Link>
            </>
          ) : (
            <p className="text-gray-600 text-sm leading-5">
              <span className="font-medium text-gray-900">{selectedLocation.name}</span>
              <br />
              {selectedLocation.address?.lineOne}
              <br />
              {selectedLocation.address?.city}, {selectedLocation.address?.state}{' '}
              {selectedLocation.address?.postalCode} <br />
              {selectedLocation.address?.country}
            </p>
          )
        ) : (
          <div className="flex flex-grow gap-x-4">
            <LocationSelector
              value={locationId}
              onChange={(location) => onLocationIdChange(location.id)}
            />

            {features.zachryIntegrationEnabled ? (
              <span
                className="flex-shrink-0 inline-block border font-normal rounded-md shadow-sm text-gray-700 border-gray-300 bg-white px-4 py-2 text-sm opacity-50 cursor-not-allowed"
                title="Locations are managed in SAP and cannot be added here."
              >
                Add Location
              </span>
            ) : (
              <Action.S className="flex-shrink-0" onClick={() => setAddingLocation(true)}>
                Add Location
              </Action.S>
            )}
          </div>
        )}

        <FieldError error={errors?.locationId} />
      </div>

      {selectedLocation && (
        <Action.T className="font-medium text-sm" onClick={() => onLocationIdChange(null)}>
          Change
        </Action.T>
      )}
    </div>
  )
}

export default LocationField
